import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";

interface ProcessingSkeletonProps {
  type: 'canvas' | 'filmstrip' | 'compare' | 'browse' | 'thumbnail';
  progress?: number;
  statusText?: string;
  documentColor?: string;
  aspectRatio?: string;
  count?: number; // For filmstrip/browse grids
}

export function ProcessingSkeleton({ 
  type, 
  progress = 0, 
  statusText = "Processing...", 
  documentColor = "#ef4444",
  aspectRatio = "3/4",
  count = 6
}: ProcessingSkeletonProps) {
  
  const renderCanvasSkeleton = () => (
    <div className="w-full h-full flex flex-col items-center justify-center p-4">
      <div 
        className="w-full max-w-full max-h-full rounded-lg border-2 border-dashed flex items-center justify-center"
        style={{ 
          aspectRatio, 
          borderColor: documentColor,
          backgroundColor: `${documentColor}10`
        }}
      >
        <div className="text-center space-y-4">
          <div 
            className="w-16 h-16 rounded-full border-4 border-t-transparent animate-spin mx-auto"
            style={{ borderColor: `${documentColor} transparent transparent transparent` }}
          />
          <div className="space-y-2">
            <p className="text-sm font-medium" style={{ color: documentColor }}>
              {statusText}
            </p>
            {progress > 0 && (
              <div className="w-48">
                <Progress value={progress} className="h-2" />
                <p className="text-xs text-muted-foreground mt-1">{progress}% complete</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );

  const renderFilmstripSkeleton = () => (
    <div className="flex flex-col space-y-3 p-4">
      <div className="flex items-center space-x-2 mb-4">
        <div 
          className="w-3 h-3 rounded-full animate-pulse"
          style={{ backgroundColor: documentColor }}
        />
        <p className="text-sm font-medium" style={{ color: documentColor }}>
          {statusText}
        </p>
      </div>
      {Array.from({ length: count }, (_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton 
            className="w-full rounded-md border-2"
            style={{ 
              aspectRatio,
              borderColor: `${documentColor}30`,
              backgroundColor: `${documentColor}10`
            }}
          />
          <Skeleton className="h-4 w-12 mx-auto" />
        </div>
      ))}
      {progress > 0 && (
        <div className="mt-4 px-2">
          <Progress value={progress} className="h-1" />
        </div>
      )}
    </div>
  );

  const renderCompareSkeleton = () => (
    <div className="grid grid-cols-2 gap-4 h-full p-4">
      <div className="flex flex-col items-center justify-center space-y-4">
        <div 
          className="w-full max-w-sm rounded-lg border-2 border-dashed flex items-center justify-center"
          style={{ 
            aspectRatio,
            borderColor: documentColor,
            backgroundColor: `${documentColor}10`
          }}
        >
          <div className="text-center space-y-3">
            <div 
              className="w-12 h-12 rounded-full border-3 border-t-transparent animate-spin mx-auto"
              style={{ borderColor: `${documentColor} transparent transparent transparent` }}
            />
            <p className="text-xs font-medium" style={{ color: documentColor }}>
              {statusText}
            </p>
          </div>
        </div>
        {progress > 0 && (
          <div className="w-full max-w-sm">
            <Progress value={progress} className="h-1" />
          </div>
        )}
      </div>
      <div className="flex items-center justify-center">
        <Skeleton className="w-full max-w-sm rounded-lg" style={{ aspectRatio }} />
      </div>
    </div>
  );

  const renderBrowseSkeleton = () => (
    <div className="p-4 space-y-4">
      <div className="flex items-center space-x-2">
        <div 
          className="w-3 h-3 rounded-full animate-pulse"
          style={{ backgroundColor: documentColor }}
        />
        <p className="text-sm font-medium" style={{ color: documentColor }}>
          {statusText}
        </p>
        {progress > 0 && (
          <div className="flex-1 max-w-xs">
            <Progress value={progress} className="h-1" />
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {Array.from({ length: count }, (_, i) => (
          <div key={i} className="space-y-2">
            <Skeleton 
              className="w-full rounded-md border-2"
              style={{ 
                aspectRatio,
                borderColor: `${documentColor}30`,
                backgroundColor: `${documentColor}10`
              }}
            />
            <Skeleton className="h-3 w-8 mx-auto" />
          </div>
        ))}
      </div>
    </div>
  );

  const renderThumbnailSkeleton = () => (
    <div className="relative">
      <Skeleton 
        className="w-full rounded-md border-2"
        style={{ 
          aspectRatio,
          borderColor: `${documentColor}30`,
          backgroundColor: `${documentColor}10`
        }}
      />
      <div className="absolute inset-0 flex items-center justify-center">
        <div 
          className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin"
          style={{ borderColor: `${documentColor} transparent transparent transparent` }}
        />
      </div>
      {statusText && (
        <div className="absolute bottom-1 left-1 right-1">
          <p className="text-xs text-center font-medium truncate" style={{ color: documentColor }}>
            {statusText}
          </p>
        </div>
      )}
    </div>
  );

  switch (type) {
    case 'canvas':
      return renderCanvasSkeleton();
    case 'filmstrip':
      return renderFilmstripSkeleton();
    case 'compare':
      return renderCompareSkeleton();
    case 'browse':
      return renderBrowseSkeleton();
    case 'thumbnail':
      return renderThumbnailSkeleton();
    default:
      return renderCanvasSkeleton();
  }
}